/**
 * Upsell de planes al final del alta — ADR-2026-07-29-upsell-planes-salir-alta-condicionado / ADR-2026-07-29-presentacion-bottom-sheet-mobile-tocar.
 *
 * Se muestra en `/configuracion-cuenta/resultados` cuando el usuario **descargó**
 * alguna de las imágenes generadas y toca "Seguir explorando Atlas": antes de
 * seguir al alta de la empresa ve el catálogo. Si no descargó nada, no aparece.
 *
 * Monta el **mismo** `PricingCatalog` que `/subscription` (no una copia), en
 * `variant="sheet"`: bottom sheet en mobile, overlay de siempre en desktop.
 *
 * **A quien YA TIENE PLAN PAGO no se le muestra.** Lo decide `abreElCatalogoDePlanes`
 * desde la pantalla de resultados: con el alta por link invertida, el que llega ahí puede
 * haber pagado hace tres minutos, y este catálogo le ofrecía el plan que acababa de
 * comprar (ADR-2026-09-22-el-cuestionario-no-le-vende-a-quien-ya-pago). Por eso el
 * `currentPlan: 'gratis'` de más abajo sigue siendo cierto para todo el que llega.
 *
 * **"Suscribirme" lleva a pagar** (ADR-2026-10-20-suscribirme-del-onboarding-cobra). Hasta
 * esa decisión era informativo: cualquier botón —suscribirse, cerrar u omitir— seguía a la
 * home, porque el usuario "todavía no tenía empresa" a la que colgarle la suscripción. Eso
 * dejó de ser cierto cuando la empresa pasó a crearse antes de llegar acá
 * (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real), y el botón quedó prometiendo algo
 * que no hacía. Ahora va a `/checkout` con el plan elegido y el país de la EMPRESA, o a
 * `/subscription` ante cualquier duda (`lib/suscribirseDesdeElOnboarding`). Cerrar u
 * omitir siguen como siempre.
 *
 * Los precios se muestran con el país **inferido del browser**
 * (`lib/paisDelBrowser`), que es una heurística de display: sin ella un usuario
 * argentino —el grueso— vería USD.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../../design-system/icons';
import PricingCatalog from './PricingCatalog';
import { buildPlanViews } from './buildPlanViews';
import { PLAN_ORDER, ULTRA_TIERS } from './pricingPlans';
import { RECOMMENDED_PLAN, type PricingPlanKey } from './pricingContent';
import { PRICING_COPY_ONBOARDING } from './pricingContexts';
import {
  getPlanById,
  getPlanPriceForPais,
  formatPriceForPais,
  paisFlags,
} from '../../lib/checkoutPlans';
import { paisDelBrowser } from '../../lib/paisDelBrowser';
import { fetchHotSaleCoupon, type AppliedCoupon } from '../../lib/hotSaleCoupon';
import { trackEvent } from '../../lib/posthog';
import { destinoDeSuscripcion, leerEmpresaParaCobrar } from '../../lib/suscribirseDesdeElOnboarding';

/** WhatsApp de soporte, el mismo que usa `/subscription`. */
const WHATSAPP_URL = `https://wa.me/5491122635392?text=${encodeURIComponent(
  'Buenas, tengo una duda sobre los planes de Atlas.',
)}`;

export interface SetupPricingSheetProps {
  /**
   * Se llama cuando el usuario sale del sheet sin suscribirse (cerrar, arrastrar hacia
   * abajo u "Omitir por ahora"). En mobile llega **después** de la animación de salida.
   * El CTA de suscripción NO pasa por acá: va a pagar.
   */
  onDismiss: () => void;
  /**
   * Corre justo antes de ir a pagar. El onboarding de marca lo usa para olvidar lo que
   * tenía guardado, igual que al salir a la home: si no, al volver retomaría el onboarding.
   */
  alIrAPagar?: () => void;
}

export function SetupPricingSheet({ onDismiss, alIrAPagar }: SetupPricingSheetProps) {
  const navigate = useNavigate();
  // Se resuelve una sola vez: el timezone no cambia mientras el sheet está
  // abierto y no queremos que un re-render mueva los precios.
  const pais = useMemo(() => paisDelBrowser() ?? '', []);
  const [selected, setSelected] = useState<PricingPlanKey>(RECOMMENDED_PLAN);
  const [tierIndex, setTierIndex] = useState(0);

  // El MISMO cupón invisible que `/subscription` y el sheet de "sin créditos"
  // (`lib/hotSaleCoupon`). Antes acá iba `coupon: null` y este catálogo —el PRIMERO
  // que ve el usuario, justo en el momento de más intención— mostraba Pro y Ultra a
  // precio de lista, mientras que los otros dos puntos de venta mostraban el descuento
  // vigente. El descuento es real y le va a aplicar cuando pague: mostrarlo acá no
  // promete nada que el checkout no cobre.
  // Ver ADR-2026-08-03-upsell-del-alta-muestra-el-mismo-descuento.
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);
  useEffect(() => {
    let cancelado = false;
    fetchHotSaleCoupon().then((c) => {
      if (!cancelado) setCoupon(c);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  // "Suscribirme": lee la empresa (país y plan de verdad) y va al checkout, o a
  // `/subscription` si algo no cierra. `procesando` evita el doble toque mientras lee.
  //
  // Si mientras se lee la persona cierra u omite (en desktop esos botones siguen activos),
  // el "Suscribirme" en camino se cancela: si no, la mandaba a pagar DESPUÉS de haber
  // elegido omitir, encima de la home a la que ya había ido (review de Greptile #193).
  const [procesando, setProcesando] = useState(false);
  const cancelado = useRef(false);
  useEffect(
    () => () => {
      cancelado.current = true;
    },
    [],
  );
  const salirSinSuscribirse = () => {
    cancelado.current = true;
    onDismiss();
  };
  const suscribirse = async (plan: PricingPlanKey) => {
    if (procesando) return;
    setProcesando(true);
    const empresa = await leerEmpresaParaCobrar();
    if (cancelado.current) return;
    const destino = destinoDeSuscripcion({ plan, ultraTierIndex: tierIndex, empresa, coupon });
    trackEvent('onboarding_suscribirme', { plan, destino: destino.ruta });
    alIrAPagar?.();
    if (destino.ruta === '/checkout') navigate('/checkout', { state: destino.state });
    else navigate('/subscription');
  };

  const plans = buildPlanViews({
    // Sin empresa no hay plan contratado: todas las cards son un alta.
    currentPlan: 'gratis',
    // Este sheet es parte del onboarding: la cuenta se acaba de crear, así que no
    // existe el caso "ex cliente que volvió a gratis" que
    // ADR-2026-08-18-descuento-no-revive-al-volver-a-gratis viene a cerrar. Se
    // pasa fijo en vez de consultar la empresa —que ni siquiera existe todavía—
    // y el checkout, que sí COBRA, lo verifica por su cuenta contra la base.
    yaFueClientePago: false,
    esArgentina: paisFlags(pais).isArgentina,
    ultraTiers: ULTRA_TIERS,
    ultraTierIndex: tierIndex,
    coupon,
    arsBaseFor: (key) => getPlanById(key)?.price ?? 0,
    getPlanPrice: (planId, ars) => getPlanPriceForPais(planId, ars, pais),
    formatPrice: (amount) => formatPriceForPais(amount, pais),
    planOrder: PLAN_ORDER,
    processing: procesando,
    // Sin plan vigente no hay nada que cancelar: `buildPlanViews` no llega a
    // usarlo (`canCancel` es false para 'gratis').
    onCancel: () => {},
  });

  return createPortal(
    <>
      <PricingCatalog
      variant="sheet"
      contextCopy={PRICING_COPY_ONBOARDING}
      plans={plans}
      selected={selected}
      onSelect={setSelected}
      onSubscribe={suscribirse}
      processing={procesando}
      onClose={salirSinSuscribirse}
      skipLabel="Omitir por ahora"
      onSkip={salirSinSuscribirse}
      ultraTier={{
        index: tierIndex,
        count: ULTRA_TIERS.length,
        onChange: setTierIndex,
      }}
      // El switch Mensual/Anual es sólo para cuentas `dev_tester` (ADR-2026-07-28-switch-mensual-anual-visual-solo) y
      // acá todavía no hay perfil de empresa que consultar.
      showBillingToggle={false}
      annual={false}
      onAnnualChange={() => {}}
      onHelp={() => window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer')}
      note={
        <div className="flex flex-col items-center gap-4">
          <p className="max-w-xl">
            Podés empezar gratis y suscribirte cuando quieras desde tu cuenta.
          </p>
          <button
            type="button"
            onClick={() => window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer')}
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-white/90"
          >
            <Icon name="outline/whatsapp" size={16} className="shrink-0" />
            ¿Tenés alguna duda? Escribinos
          </button>
        </div>
      }
      />
    </>,
    // Portal a `body`: el shell de la app (`DashboardLayout`, que envuelve
    // resultados en desktop) es `fixed` + `transform-gpu`, y un `fixed` adentro
    // de un ancestro con transform se posiciona respecto de ÉL, no del viewport
    // (mismo motivo que en `Paywall`, ADR-2026-07-28-chrome-paywall-va-anclado-overlay y ADR-2026-07-29-catalogo-paywall-modales-comparten-z).
    document.body,
  );
}

export default SetupPricingSheet;
